// @ts-expect-error
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';

export const DROP_PIN_FALL_OVERLAY_WIDTH_PX = 24;
export const DROP_PIN_FALL_OVERLAY_HEIGHT_PX = 36;
export const DROP_PIN_FALL_OVERLAY_OFFSET_PX = 64;
export const DROP_PIN_FALL_OVERLAY_DURATION_MS = 520;

export const DROP_PIN_FALL_OVERLAY_TIP_Y_PX = 36;
/** Extra settle after playback split padding is applied. */
export const DROP_PIN_POST_SPLIT_SETTLE_MS = 72;
export const DROP_PIN_RECENTER_DURATION_MS = 320;
/** Cap map-tile settle waits so drop never stalls on slow layers. */
export const DROP_PIN_MAP_SETTLE_TIMEOUT_MS = 900;
/** Extra hold after fall so the map graphic is painted before HTML dismiss. */
export const DROP_PIN_MAP_HANDOFF_MS = 200;
/** Soft ease-out after a quick start — reads as a clean drop without idle frames. */
export const DROP_PIN_FALL_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
/** Ground shadow under the landing tip (matches SurveyLayerPanel pulse). */
export const DROP_PIN_FALL_SHADOW_WIDTH_PX = 28;
export const DROP_PIN_FALL_SHADOW_HEIGHT_PX = 12;

export type DropPinFallOverlayOptions = {
  readonly clientX: number;
  readonly clientY: number;
  readonly iconUrl: string;
  readonly fallOffsetPx?: number;
  readonly durationMs?: number;
  readonly prefersReducedMotion?: boolean;
};

/** Holds the landed pin until the map graphic is ready, then dismiss. */
export type DropPinFallOverlayHandle = {
  readonly dismiss: () => Promise<void>;
};

export type SettlingMapView = {
  readonly updating?: boolean;
};

function buildPinTransform(clientX: number, clientY: number): string {
  const x = clientX - DROP_PIN_FALL_OVERLAY_WIDTH_PX / 2;
  const y = clientY - DROP_PIN_FALL_OVERLAY_TIP_Y_PX;
  return `translate3d(${x}px, ${y}px, 0)`;
}

function buildShadowTransform(clientX: number, clientY: number, scale: number): string {
  const x = clientX - DROP_PIN_FALL_SHADOW_WIDTH_PX / 2;
  const y = clientY - DROP_PIN_FALL_SHADOW_HEIGHT_PX / 2;
  return `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
}

/**
 * Important: never cancel the fall WAAPI — canceling reverts to the start
 * transform and looks like the pin flying back up / home.
 */
export async function runDropPinFallOverlay(
  options: DropPinFallOverlayOptions,
): Promise<DropPinFallOverlayHandle> {
  const noopHandle: DropPinFallOverlayHandle = {
    dismiss: async () => undefined,
  };
  const {
    clientX,
    clientY,
    iconUrl,
    fallOffsetPx = DROP_PIN_FALL_OVERLAY_OFFSET_PX,
    durationMs = DROP_PIN_FALL_OVERLAY_DURATION_MS,
    prefersReducedMotion = false,
  } = options;

  if (
    typeof document === 'undefined' ||
    !Number.isFinite(clientX) ||
    !Number.isFinite(clientY)
  ) {
    return noopHandle;
  }

  const startY = clientY - Math.max(0, fallOffsetPx);
  const startTransform = buildPinTransform(clientX, startY);
  const endTransform = buildPinTransform(clientX, clientY);

  const host = document.createElement('div');
  host.setAttribute('data-xa-drop-pin-fall-overlay', '1');
  host.setAttribute('aria-hidden', 'true');
  Object.assign(host.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483646',
    pointerEvents: 'none',
    overflow: 'visible',
  } as Partial<CSSStyleDeclaration>);

  const shadow = document.createElement('div');
  shadow.setAttribute('data-xa-drop-pin-fall-shadow', '1');
  Object.assign(shadow.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${DROP_PIN_FALL_SHADOW_WIDTH_PX}px`,
    height: `${DROP_PIN_FALL_SHADOW_HEIGHT_PX}px`,
    borderRadius: '50%',
    pointerEvents: 'none',
    transform: buildShadowTransform(clientX, clientY, 0.55),
    transformOrigin: '50% 50%',
    opacity: '0.35',
    willChange: 'transform, opacity',
    background:
      'radial-gradient(ellipse at center, rgba(11, 39, 64, 0.42) 0%, rgba(11, 39, 64, 0.18) 48%, rgba(11, 39, 64, 0) 74%)',
    boxShadow:
      '0 0 0 1px rgba(58, 134, 255, 0.28), 0 0 12px 3px rgba(58, 134, 255, 0.32)',
  } as Partial<CSSStyleDeclaration>);

  const pin = document.createElement('div');
  pin.setAttribute('data-xa-drop-pin-fall-icon', '1');
  Object.assign(pin.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${DROP_PIN_FALL_OVERLAY_WIDTH_PX}px`,
    height: `${DROP_PIN_FALL_OVERLAY_HEIGHT_PX}px`,
    pointerEvents: 'none',
    transform: startTransform,
    transformOrigin: '50% 100%',
    willChange: 'transform, opacity',
    contain: 'layout style',
    backgroundImage: `url("${iconUrl}")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    opacity: '1',
  } as Partial<CSSStyleDeclaration>);

  host.appendChild(shadow);
  host.appendChild(pin);
  document.body.appendChild(host);

  let dismissed = false;
  const dismiss = async () => {
    if (dismissed) {
      return;
    }
    dismissed = true;
    shadow.style.willChange = 'auto';
    pin.style.willChange = 'auto';
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  };

  if (
    prefersReducedMotion ||
    typeof pin.animate !== 'function' ||
    fallOffsetPx <= 0
  ) {
    await dismiss();
    return noopHandle;
  }

  // Paint the start pose before animating so the first frame isn't a jump.
  await waitForNextPaint();

  await new Promise<void>((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      shadow.style.opacity = '0';
      pin.style.transform = endTransform;
      resolve();
    };

    shadow.animate(
      [
        {
          transform: buildShadowTransform(clientX, clientY, 0.55),
          opacity: 0.35,
          offset: 0,
        },
        {
          transform: buildShadowTransform(clientX, clientY, 1.05),
          opacity: 0.75,
          offset: 0.55,
        },
        {
          transform: buildShadowTransform(clientX, clientY, 1.22),
          opacity: 0.95,
          offset: 0.9,
        },
        {
          transform: buildShadowTransform(clientX, clientY, 1.05),
          opacity: 0,
          offset: 1,
        },
      ],
      {
        duration: durationMs,
        easing: DROP_PIN_FALL_EASING,
        fill: 'forwards',
      },
    );

    const fallAnim = pin.animate(
      [
        { transform: `${startTransform} rotate(8deg)` },
        { transform: `${endTransform} rotate(0deg)` },
      ],
      {
        duration: durationMs,
        easing: DROP_PIN_FALL_EASING,
        fill: 'forwards',
      },
    );
    fallAnim.onfinish = finish;
    fallAnim.oncancel = finish;
  });

  return { dismiss };
}

export function waitForNextPaint(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        resolve();
      });
    });
  });
}

export function waitForTimeout(ms: number): Promise<void> {
  if (typeof window === 'undefined' || ms <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Wait for React commit + playback padding inset without stacking long idle delays. */
export async function waitForPlaybackSplitReady(): Promise<void> {
  await waitForTimeout(0);
  await waitForNextPaint();
  await waitForTimeout(DROP_PIN_POST_SPLIT_SETTLE_MS);
}

/** Decode the pin asset so the fall overlay never shows an empty first frame. */
export function preloadDropPinImage(iconUrl: string): Promise<void> {
  if (typeof Image === 'undefined' || !iconUrl) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };

    const img = new Image();
    img.onload = finish;
    img.onerror = finish;
    img.decoding = 'async';
    img.src = iconUrl;

    if (img.complete) {
      finish();
      return;
    }
    if (typeof img.decode === 'function') {
      img.decode().then(finish).catch(finish);
    }
    // Cap wait so jsdom / stalled loads cannot delay the drop path.
    if (typeof window !== 'undefined') {
      window.setTimeout(finish, 250);
    } else {
      finish();
    }
  });
}

/**
 * Brief map settle for drop sequencing. Caps wait so slow tiles cannot block the fall.
 */
export async function waitForDropMapSettled(
  mapView: SettlingMapView,
  timeoutMs = DROP_PIN_MAP_SETTLE_TIMEOUT_MS,
): Promise<void> {
  await waitForNextPaint();
  if (!mapView.updating) {
    return;
  }

  await Promise.race([
    reactiveUtils.whenOnce(() => !mapView.updating),
    waitForTimeout(timeoutMs),
  ]);
}

export type MapExtentLike = {
  readonly xmin: number;
  readonly ymin: number;
  readonly xmax: number;
  readonly ymax: number;
};

/** True when the point sits near/outside the padded visible extent. */
export function isMapPointOutsidePaddedExtent(
  point: { readonly x: number; readonly y: number },
  extent: MapExtentLike,
  padRatio = 0.1,
): boolean {
  const width = extent.xmax - extent.xmin;
  const height = extent.ymax - extent.ymin;
  if (!(width > 0) || !(height > 0)) {
    return true;
  }
  const padX = width * padRatio;
  const padY = height * padRatio;
  return (
    point.x < extent.xmin + padX ||
    point.x > extent.xmax - padX ||
    point.y < extent.ymin + padY ||
    point.y > extent.ymax - padY
  );
}
